/**
 * The source store, off the main thread.
 *
 * Decision `0043`: *"move **both** source hashing and the IndexedDB write
 * behind one Worker boundary owned by `packages/store`."* Not the demo — *"every
 * caller of the persistent blob store must receive the same non-blocking
 * contract, and the demo must not grow a second storage implementation."*
 *
 * ## What it is worth, measured before it was built
 *
 * A 64 MiB source through the real door, on this machine:
 *
 * ```
 *                          main-thread stall
 * on the main thread            555-584ms      against a 1,824ms budget
 * through this Worker             4-18ms
 * ```
 *
 * The work does not get cheaper — the Worker still spends ~95ms hashing and
 * ~145ms writing — it stops being spent where a person can feel it. On the
 * deployed machine the same phases measure 1,110-1,258ms and 1,356-1,442ms, so
 * the amount moved there is about ten times larger than the amount moved here.
 *
 * ## The bytes come back, and that is not optional
 *
 * `postMessage(msg, [buffer])` DETACHES the sender's array: `byteLength` is `0`
 * on the next line. `ingest.ts` uses its source after the store returns — it
 * mints an object URL from it and decodes it for a thumbnail — so a transfer
 * that did not return the buffer would trade a 2.5s freeze for a broken ingest.
 * Every reply therefore transfers the same buffer back, and `putOwned` hands it
 * to the caller. Zero copies in either direction.
 *
 * ## The surface is declared, not imported
 *
 * The same discipline `indexeddb.ts` states for `IdbFactoryLike`, for the same
 * reason: this package compiles without the DOM or WebWorker libs, and naming
 * the four calls used here is what keeps the injection point small enough to be
 * worth injecting. A worker that can only run in a browser can only be tested
 * in a browser, and a storage path whose failure mode is "the file did not
 * survive" is the last place to accept that.
 */

import type { BlobRef, BlobStore } from './blobs.js';
import { acceptedMime } from './blobs.js';

/* ────────────────────────────────── the slice of Worker this adapter touches */

/** What the main side holds. `terminate` is optional because a worker that
 *  lives as long as the page never needs it, and a test double should not have
 *  to pretend otherwise. */
export interface WorkerLike {
  postMessage(message: unknown, transfer?: readonly unknown[]): void;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  terminate?(): void;
}

/** What the worker side is handed — `self`, in a real one. */
export interface WorkerScopeLike {
  onmessage: ((event: { data: unknown }) => void) | null;
  postMessage(message: unknown, transfer?: readonly unknown[]): void;
}

/* ───────────────────────────────────────────────────────────── the protocol */

/** One request. `seq` rather than the blob id, because the id is not known
 *  until the worker has hashed the bytes — which is the whole point. */
interface StoreRequest {
  readonly morphSourceStore: 'put';
  readonly seq: number;
  readonly bytes: Uint8Array<ArrayBuffer>;
  readonly mime: string;
}

interface StoreReply {
  readonly morphSourceStore: 'put';
  readonly seq: number;
  readonly ref?: BlobRef;
  /** Always present on success, and always the SAME buffer the request
   *  carried. Absent on failure, in which case the caller's bytes are gone —
   *  see `SourceTransferLost`. */
  readonly bytes?: Uint8Array<ArrayBuffer>;
  /**
   * What the write cost inside the worker, in ms.
   *
   * A worker has its OWN performance timeline, so `digestOf`'s
   * `morph.store.digest` measure is invisible to the page — and moving the
   * digest into a worker therefore deleted a phase from the diagnostic table.
   * Measured: `e2e/ingest-budget.mjs` went to `missing: ["digest"]` the first
   * time this ran.
   *
   * Decision `0043` clause 4 forbids exactly that — *"The phase report still
   * names `read`, `digest`, `store-source`, `write`, and `repaint`, and
   * separately identifies the Worker source-store interval"* — so the numbers
   * come back and the main side re-emits them on the page's timeline.
   */
  readonly spent?: {
    readonly digest: number;
    readonly total: number;
    /**
     * How many bytes this request actually STORED, from the store's own answer.
     *
     * Decision `0046`'s work control asks that *"the instrumented Worker
     * reports the two source-store requests and their full byte counts, so
     * bypassing hash, transfer, or IndexedDB storage makes the control red
     * without relying on timing ratios"* — and is explicit about why it cannot
     * be a duration: *"caching, Worker startup, IndexedDB scheduling, and
     * shared-runner contention make a duration ratio a machine benchmark rather
     * than proof that the bytes were stored."*
     *
     * Taken from `ref.bytes`, the count the blob store returns, rather than
     * from `bytes.byteLength` of the incoming buffer — and the honest statement
     * of what that buys is smaller than the one written here first.
     *
     * It does NOT make a lying worker detectable on its own. Measured:
     * substituting `bytes.byteLength` reddens nothing, because the two are
     * equal on every successful put and a failed one posts no `spent` at all.
     * What actually stops a worker acknowledging without storing is that this
     * whole object is built after `await blobs.put` and `ref` is required — a
     * structural fact, not a property of which length is read.
     *
     * `ref.bytes` stays because it is the store's own answer and the reply's
     * other fields already come from there, so a future store that stored
     * something other than what it was handed would say so here. Recorded as a
     * preference rather than as a control, because a comment claiming a
     * substitution that comes back green is the defect this repository keeps
     * catching in its own records.
     */
    readonly bytes: number;
  };
  readonly failed?: string;
}

/** Posted by the worker as soon as it is installed. A `404`, a bad MIME type
 *  or a module that throws while evaluating all produce a `Worker` object that
 *  never sends this — see `sourceStoreOverWorker`. */
interface ReadyNote { readonly morphSourceStore: 'ready' }

const isReady = (v: unknown): v is ReadyNote =>
  !!v && typeof v === 'object' && (v as ReadyNote).morphSourceStore === 'ready';

const isReply = (v: unknown): v is StoreReply =>
  !!v && typeof v === 'object'
  && (v as StoreReply).morphSourceStore === 'put'
  && typeof (v as StoreReply).seq === 'number';

const isRequest = (v: unknown): v is StoreRequest =>
  !!v && typeof v === 'object'
  && (v as StoreRequest).morphSourceStore === 'put'
  && typeof (v as StoreRequest).seq === 'number';

/**
 * A failure that took the caller's buffer with it.
 *
 * Named rather than a bare `Error`, because the recovery differs: an ordinary
 * store failure leaves the caller holding its bytes and free to retry, and this
 * one does not. `ingest.ts` has already read the file, so a retry means reading
 * it again — a decision for the caller, which can only make it if the two are
 * distinguishable.
 *
 * `0043`: *"cancellation or Worker failure must not be reported as a stored
 * source."* This is that, as a type.
 */
export class SourceTransferLost extends Error {
  constructor(readonly because: string) {
    super(`the source store failed after taking the bytes: ${because}`);
    this.name = 'SourceTransferLost';
  }
}

/**
 * The worker refused BEFORE it took anything, and handed the bytes back.
 *
 * @Codex, P2 on #184: *"When the worker module has announced readiness but its
 * lazy `open()` rejects, no blob-store operation has run and the transferred
 * `bytes` array is still intact inside the worker. This failure reply
 * nevertheless omits it, so `send()` converts the result to
 * `SourceTransferLost`; the demo's `putOwned()` path then rejects the first
 * file instead of retrying through the already-open `inner` store, forcing the
 * person to select the file again for a transient worker-connection failure."*
 *
 * `SourceTransferLost` means *the bytes are gone and there is nothing to retry
 * with* — the one thing that makes the wrapper's fallback safe. The open
 * failure is the opposite case and was wearing that type anyway, so the first
 * file of a session paid for a database that would not open while every file
 * after it was served by `inner`. Exactly the shape two earlier rounds already
 * fixed for a worker that never loaded and for one that died: **the file that
 * discovers a problem should not be the one that pays for it.**
 *
 * It carries the array rather than a flag, because the caller's own copy is
 * detached by then — the retry has to be run against the buffer that came
 * back, and an error that says "retryable" without supplying it would be a
 * promise the catch cannot keep.
 */
export class SourceNotWritten extends Error {
  constructor(readonly because: string, readonly bytes: Uint8Array<ArrayBuffer>) {
    super(`the source store refused before taking the bytes: ${because}`);
    this.name = 'SourceNotWritten';
  }
}

/**
 * How long the handshake waits before deciding the worker is not coming.
 *
 * **Measured, not guessed — and the first guess was 5,000.** @Codex drove the
 * deployed preview twice and instrumented the native `Worker` constructor,
 * timing construction to the first ready note:
 *
 * ```
 * f0379c1, store-worker.98ab0f564cd0.js      185.2ms
 * 4a74aa3, store-worker.9d0acdbc3093.js    1,125.4ms
 * 723b83f, store-worker.8e7b6a6e0064.js      522.9ms
 * ```
 *
 * The same module on the same infrastructure, 6× between the fastest and the
 * slowest. 5,000 was 4.4× the slowest — inside the spread already observed, so
 * a colder run could have reached it.
 *
 * The third measurement landed between the other two and is the reason this
 * number is not moving again: 15,000 is 13× the slowest of three, and three
 * observations spanning 185-1,125ms describe a distribution rather than a pair.
 *
 * The two ways of being wrong are not symmetric, which is what decides the
 * number. Expiring EARLY on a healthy worker costs exactly one file the
 * main-thread ingest it would have had before `0043` — the store is complete
 * either way, the worker is taken back the moment it speaks, and nobody can
 * tell. Expiring LATE on a worker that will never answer costs a person
 * staring at a file that is not moving. The first is invisible and bounded;
 * the second is visible and also bounded.
 *
 * So: generous against the spread, at 13× the slowest handshake anyone has
 * measured. A worker that has not spoken in fifteen seconds is not slow.
 */
export const READY_WITHIN_MS = 15_000;

/**
 * How long a request may wait after the worker has taken its bytes.
 *
 * @Codex, P2 on #184: *"When the worker announces readiness but later stops
 * responding — for example, because its `open()` or IndexedDB write never
 * settles — this promise remains in `waiting` forever: the readiness deadline
 * has already completed, and `onerror` is not guaranteed for a hung
 * asynchronous operation. The file's buffer has already been transferred, so
 * ingestion cannot fall back and the UI remains stuck in `store-source`."*
 *
 * Right, and it is the handshake deadline's own defect one step later: that
 * one covered a worker that never speaks, and this covers one that speaks once
 * and then stops. Both are the same failure — a promise nobody will settle —
 * and only the moment differs.
 *
 * It scales, because the work does. Deployed, a 64 MiB write spent 2,280 and
 * 2,512ms inside the worker; locally 352 and 579ms. This gives that file 42
 * seconds, about 17× the slowest deployed observation.
 *
 * **Deliberately generous, and the asymmetry is the opposite of the
 * handshake's.** The bytes are gone by the time this can fire, so a request
 * that gives up reports `SourceTransferLost` — and if the write then completes,
 * that report was false. A person told their file was lost when it was saved is
 * worse than one waiting a little longer for a machine that is genuinely slow.
 */
const REPLY_BASE_MS = 10_000;
const REPLY_PER_MIB_MS = 500;
const MIB = 1024 * 1024;
const replyWithin = (bytes: number): number =>
  REPLY_BASE_MS + Math.ceil(bytes / MIB) * REPLY_PER_MIB_MS;

/**
 * The worker never answered — and the bytes were never sent.
 *
 * Deliberately NOT a `SourceTransferLost`: that type means the caller's buffer
 * is detached and there is nothing left to retry with. Here the handshake sits
 * before the transfer, so the caller still owns every byte and the fallback to
 * `inner` is lossless. The two types exist to tell those cases apart, and a
 * deadline that reported the wrong one would throw away a file it still had.
 */
export class SourceWorkerStalled extends Error {
  constructor(readonly because: string) {
    super(`the source store worker never answered: ${because}`);
    this.name = 'SourceWorkerStalled';
  }
}

/* ──────────────────────────────────────────────────────────── the main side */

/**
 * Wrap a blob store so that storing a SOURCE goes through the worker.
 *
 * Reads stay on this thread deliberately. `0043` moves the byte-proportional
 * write, and only the write: `get`, `stat` and `list` are what a repaint calls,
 * they are already cheap, and routing them through a message port would add a
 * round trip to every one of them. `derivatives` likewise — a thumbnail is a
 * few kilobytes, and its index is a compare-and-set whose atomicity rests on a
 * transaction this thread owns.
 *
 * The consequence is TWO connections to one database, which is exactly what
 * IndexedDB serializes `readwrite` transactions across. `fake-indexeddb.ts`
 * models that as of task `0123`, so the tests under this can see a conflict the
 * browser would produce rather than one it would not.
 */
export function sourceStoreOverWorker(
  inner: BlobStore, worker: WorkerLike, readyWithin = READY_WITHIN_MS,
  // Injectable for the same reason `readyWithin` is: a clause about a worker
  // that stops answering cannot wait ten seconds to say so.
  answerWithinFor: (bytes: number) => number = replyWithin,
): BlobStore {
  let seq = 0;
  /**
   * Set once the worker has failed, and cleared only for a worker that was
   * slow rather than absent.
   *
   * @Codex, P2 on #184: *"the handler rejects only requests already in
   * `waiting` and leaves the adapter usable. A later `put()` adds a new pending
   * entry and posts to the same dead worker; that post can be silently
   * discarded, so the ingest waits forever instead of reporting
   * `SourceTransferLost`."*
   *
   * Right, and it is the same failure the `onerror` handler was added to
   * prevent, one request later — a hang that is indistinguishable from a slow
   * ingest. The handler fixed the calls that were already in flight and left
   * every call after them to hang, which is the worse half: the first is a
   * person's file, the rest are every file for the life of the page.
   *
   * No attempt to recreate the worker. This adapter is handed
   * one and does not know how to make another — the URL, the module type and
   * the database name are all the demo's. Recreation belongs where construction
   * does, and a failure that reports itself every time is a thing a caller can
   * act on; one that half-works is not.
   */
  let dead: string | undefined;
  /**
   * Resolved when the worker says it is installed, rejected when it fails.
   *
   * @Codex, P2 on #184, on `store-setup.ts`: *"When the hashed worker asset
   * returns 404 or its module fails during evaluation, `new Worker()` normally
   * succeeds and reports the failure asynchronously, so this `catch` never
   * selects `inner`. The returned wrapper's `onerror` handler instead marks the
   * adapter permanently dead; the first upload loses its transferred bytes and
   * every later upload is rejected for the lifetime of the page."*
   *
   * Both halves are right and the first one is mine twice over: the demo's
   * `try/catch` around `new Worker` is decorative for the likeliest failure,
   * and its comment claimed *"a 404 on the worker URL … ends the same way — the
   * unwrapped store"*, which was false. A constructor that does not throw
   * cannot be caught.
   *
   * So nothing is transferred until the worker has spoken. `send` awaits this
   * first, and a caller's bytes are still its own if the wait fails.
   */
  let announce: () => void;
  let disown: (why: Error) => void;
  const ready = new Promise<void>((ok, no) => { announce = ok; disown = no; });
  // Never unhandled: `send` may reject before anyone awaits `ready`, and an
  // unobserved rejection is a process warning in Node and noise in a browser.
  ready.catch(() => undefined);

  /**
   * And the handshake has an UPPER BOUND, because silence is a third outcome.
   *
   * @Codex, P2 on #184: *"the readiness wait is unbounded. A worker whose
   * script never finishes fetching or evaluating produces neither the ready
   * note nor an `error` event, so `onerror` never fires, `dead` is never set,
   * and the first `putOwned()` awaits a promise that will not settle. Ingest
   * hangs for the lifetime of the page."*
   *
   * Right, and it is the previous round's fix reasoning from two outcomes when
   * there are three. That round made `send` wait for the worker to speak so
   * the first file keeps its bytes when the worker never loads — and a worker
   * that never loads *loudly* does fire `onerror`. A stalled fetch says
   * nothing at all, and waiting for a promise nobody will settle is the one
   * failure the person cannot even be told about.
   *
   * Falling back is not a refusal: `inner` is a complete `BlobStore`, so the
   * cost of being wrong here is that ingest blocks the main thread exactly as
   * much as it did before the worker existed. The cost of being right is a
   * file that lands instead of a page that never answers.
   *
   * Recorded in `dead` so the SECOND file does not pay the deadline over
   * again — and cleared below if the worker turns out to have been merely
   * slow, because a permanent refusal for a transient stall is the overshoot
   * this file has already been caught making once.
   */
  let stalled: string | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  /**
   * A RECOVERABLE unavailability, as against `onerror`'s terminal one.
   *
   * Two things can make the worker unusable without proving it is gone: the
   * handshake not arriving in time, and a request it accepted never being
   * answered. Both mean "do not send it the next file"; neither means "never
   * send it another file", because a slow load and a slow write both end with
   * the worker speaking.
   *
   * `onerror`'s `dead` outranks this and is never cleared by it — that one is
   * an event saying the worker failed, and no later message can unsay it.
   */
  const stall = (why: string): void => {
    if (dead !== undefined && dead !== stalled) return;   // a terminal death wins
    stalled = why;
    dead = why;
  };
  const spoke = (): void => {
    if (dead !== undefined && dead === stalled) { dead = undefined; stalled = undefined; }
  };
  const spoken = new Promise<never>((_, no) => {
    timer = setTimeout(() => {
      const why = `the worker did not answer within ${readyWithin}ms`;
      stall(why);
      no(new SourceWorkerStalled(why));
    }, readyWithin);
  });
  spoken.catch(() => undefined);

  const waiting = new Map<number, {
    ok(reply: StoreReply): void; fail(why: Error): void;
    /**
     * This request's OWN allowance — `answerWithinFor(bytes)` and nothing
     * added — so a later one can sum what is ahead of it in the worker's queue.
     *
     * @Codex, P2 on `41e9da0`: *"each `waiting` entry stores its
     * already-cumulative `answerWithin`, so this reduction recursively counts
     * predecessors multiple times: equal requests receive deadlines of B, 2B,
     * 4B, 8B rather than B, 2B, 3B, 4B. If the serialized worker hangs after
     * ten small uploads have been posted, the tenth can remain pending for
     * roughly 90 minutes before reporting loss."*
     *
     * Exactly right, and it is my own fix from an hour earlier compounding:
     * the field was written as *"this request's own allowance"* and then set
     * from the summed value two lines later. A comment describing a variable
     * the code does not hold is the shape this repository keeps catching, and
     * this one wrote the description first.
     *
     * The bound it produced is not merely generous — it doubles per queued
     * file, so a hung worker's tenth upload waits 512 times its own deadline.
     * A deadline that grows exponentially with traffic is not a deadline.
     */
    readonly allowance: number;
  }>();

  worker.onmessage = (event) => {
    if (isReady(event.data)) {
      if (timer !== undefined) clearTimeout(timer);
      // Merely slow, not stalled: the deadline already sent one file down the
      // main-thread path, and every file after it can have the worker back.
      // Only a deadline of our own is cleared — `onerror` is terminal.
      spoke();
      announce();
      return;
    }
    if (!isReply(event.data)) return;
    const pending = waiting.get(event.data.seq);
    if (!pending) {
      /**
       * A LATE reply, and it is the proof of recovery.
       *
       * The request it belongs to gave up and its bytes are gone, so there is
       * nobody to hand this to. But a worker that answers at all is a worker
       * that is draining its queue — which is exactly the fact the stall was
       * standing in for, and the only evidence that can retire it.
       */
      spoke();
      return;
    }
    waiting.delete(event.data.seq);
    pending.ok(event.data);
  };
  /**
   * A worker that dies takes every outstanding call with it.
   *
   * Without this they hang forever, and a hung ingest is indistinguishable from
   * a slow one — the failure mode `0043`'s clause 2 is about, arriving through
   * the transport rather than through the store.
   */
  worker.onerror = (event) => {
    dead = `the worker failed: ${describe(event)}`;
    disown(new Error(dead));
    const why = new SourceTransferLost(dead);
    for (const [at, pending] of waiting) { waiting.delete(at); pending.fail(why); }
  };

  const send = async (
    bytes: Uint8Array<ArrayBuffer>, mime: string,
  ): Promise<{ ref: BlobRef; bytes: Uint8Array<ArrayBuffer> }> => {
    /**
     * The type, BEFORE the transfer — and this is the same rule as the
     * handshake below, applied to the other thing that can refuse.
     *
     * @Codex, P2 on #184: *"the outer Worker's `putOwned()` path still
     * transfers the caller's buffer before that validation runs. With a
     * noncanonical MIME such as `IMAGE/PNG`, the worker's inner store rejects,
     * its failure reply omits the still-intact buffer, and the caller receives
     * `SourceTransferLost` with its original array detached; the direct
     * backends instead reject before taking the bytes."*
     *
     * Right, and it is this morning's `acceptedMime` fix one dimension short:
     * both backends got the check and the boundary in front of them did not,
     * so the one caller whose buffer is destroyed by a refusal is the one
     * going through the Worker. `0047` calls `putOwned` a public boundary; a
     * public boundary that destroys the value it refuses is worse than one
     * that never validated, because the caller cannot retry.
     *
     * It belongs in `send` rather than in each wrapper: `put` also reaches the
     * worker, and validating there too costs a rejected call the allocation
     * and copy of a payload that was never going to be stored.
     */
    acceptedMime(mime);
    /**
     * The handshake, BEFORE the transfer.
     *
     * A worker that never loads reaches here with `ready` already rejected, so
     * this throws and the caller still holds its file. The old order lost the
     * first upload's bytes to a worker that had never existed.
     */
    await Promise.race([ready, spoken]);
    /**
     * And AGAIN after the await, which is not belt-and-braces.
     *
     * `await ready` yields. A worker that fails in that window has already run
     * `onerror` — so `dead` is set and every entry in `waiting` was failed —
     * but this call was not in `waiting` yet, so nothing failed it. It would
     * then post to a dead worker and hang: the exact defect the terminal memory
     * was added to prevent, moved into the gap the handshake opened.
     *
     * Found by a clause written for the handshake, which timed out at 5s
     * instead of rejecting.
     *
     * An ORDINARY error, not `SourceTransferLost`. @Codex, P2 on #184: *"this
     * branch runs before `postMessage()`, so the caller still owns an intact
     * buffer. Reporting `SourceTransferLost` makes `putOwned()` rethrow
     * instead of falling back to `inner`, unnecessarily refusing the upload
     * even though no transfer or store attempt occurred."*
     *
     * The type is a claim about the BYTES and it was being chosen by where in
     * the function the failure happened rather than by what had been done to
     * them. Everything above the `postMessage` below leaves the caller's array
     * where it was, so everything above it is retryable — and `putOwned`'s
     * catch already writes such a call through `inner` using the array it
     * still holds.
     */
    if (dead !== undefined) throw new Error(dead);
    const at = seq++;
    const sentAt = now();
    /**
     * This file's allowance PLUS everything already queued ahead of it.
     *
     * @Codex, P2 on `857dbfa`: *"When two uploads run concurrently and a large
     * first write keeps the serialized worker queue busy, this timer starts as
     * soon as the second request is posted even though its write has not
     * begun. Because the deadline scales only with that second payload, a tiny
     * file gets about 10.5 seconds while the preceding 64 MiB write is allowed
     * 42 seconds; if the first healthy write takes 12 seconds, the second is
     * reported as `SourceTransferLost` and never attached even though the
     * worker subsequently stores it."*
     *
     * Two facts that were each true and never put together: the worker
     * serialises its requests — which is why one hung write poisons the queue,
     * a defect already fixed two rounds ago — and the deadline is a function of
     * ONE payload's size. So a small file behind a large one is measured
     * against a clock that started before its work could.
     *
     * The sum is the cheapest of the three shapes the review offered, and the
     * one that needs no new message: what is ahead of this request is exactly
     * what is in `waiting`, and each entry already knows what it was allowed.
     * A start acknowledgement would be a second round trip per file; serialising
     * on the main side would make the queue's cost visible to callers that do
     * not have it today.
     *
     * It is an UPPER bound and deliberately so — an entry that finishes while
     * this one is posting has already paid its own allowance and this one is
     * merely generous. Too generous means a hang is reported late; too mean
     * means a stored file is reported lost, which is the direction that costs
     * somebody their upload.
     *
     * Read BEFORE the post, because `postMessage` detaches the buffer and
     * `bytes.byteLength` is 0 on the far side of it.
     */
    const own = answerWithinFor(bytes.byteLength);
    const ahead = [...waiting.values()].reduce((sum, e) => sum + e.allowance, 0);
    const answerWithin = own + ahead;
    const reply = await new Promise<StoreReply>((ok, no) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      // Both settlement paths clear it — including `onerror`, which reaches
      // this entry through `waiting` and would otherwise leave a timer running
      // against a promise that is already rejected.
      const settle = <T>(then: (value: T) => void) => (value: T): void => {
        if (timer !== undefined) clearTimeout(timer);
        then(value);
      };
      // `own`, not `answerWithin`. Storing the cumulative value made each
      // entry count its predecessors again for the next one — B, 2B, 4B, 8B.
      waiting.set(at, { ok: settle(ok), fail: settle(no), allowance: own });
      try {
        worker.postMessage(
          { morphSourceStore: 'put', seq: at, bytes, mime } satisfies StoreRequest,
          [bytes.buffer],
        );
        /**
         * And a deadline on the ANSWER, not only on the handshake.
         *
         * `SourceTransferLost` rather than an ordinary error, and that is the
         * whole reason this is a different constant from the handshake's: the
         * bytes were transferred by the line above, so there is nothing to fall
         * back with. A caller that retried would be retrying with a detached
         * buffer.
         */
        timer = setTimeout(() => {
          waiting.delete(at);
          /**
           * And the worker is marked unavailable, not merely this request.
           *
           * @Codex, P2 on #184: *"When the first timeout is caused by a
           * permanently hung `blobs.put()`, the worker-side queue remains
           * blocked on that request, but this callback only removes its
           * main-thread waiter and does not mark the worker unavailable. Every
           * subsequent upload is therefore transferred into the same blocked
           * queue, loses its bytes, and times out instead of falling back to
           * `inner`."*
           *
           * Right, and worse than a missed improvement: the clause I wrote for
           * this deadline asserted the second file ALSO rejecting, and called
           * that "the adapter is not poisoned". It was pinning the defect. The
           * worker serialises its requests, so one hung write blocks every file
           * behind it — and each of those pays with its bytes to discover a
           * fact the first one already established.
           *
           * Recoverable, through `spoke()`: a late reply proves the queue is
           * draining and the worker gets the next file.
           */
          const why = `the worker took the bytes and did not answer within ${answerWithin}ms`;
          stall(why);
          no(new SourceTransferLost(why));
        }, answerWithin);
      } catch (e) {
        // The post itself threw, so the buffer was NOT transferred and the
        // caller still has it. An ordinary failure, not a lost one.
        if (timer !== undefined) clearTimeout(timer);
        waiting.delete(at);
        no(e instanceof Error ? e : new Error(String(e)));
      }
    });
    /**
     * A failure that RETURNED the buffer is retryable; one that did not is not.
     *
     * The reply's own shape carries the distinction, so the worker decides it
     * at the place that knows — before its `put`, the array is untouched and
     * comes back; after, it may be detached, half-cloned or simply
     * untrustworthy, and the worker deliberately keeps it. @Codex, P2 on #184.
     */
    if (reply.failed !== undefined) {
      if (reply.bytes) throw new SourceNotWritten(reply.failed, reply.bytes);
      throw new SourceTransferLost(reply.failed);
    }
    if (!reply.ref || !reply.bytes) {
      throw new SourceTransferLost('the worker replied without a result');
    }
    /**
     * The worker's timeline, replayed onto the page's.
     *
     * Decision `0043` clause 4: the phase report must still name `digest`, and
     * *"separately identify the Worker source-store interval"*. Both are here —
     * `morph.store.digest` is the same name `digestOf` emits on the main
     * thread, so nothing downstream needs to know where the hashing happened,
     * and `morph.store.worker` is the new one that says it happened elsewhere.
     *
     * Emitted with an explicit `start` and `duration` rather than by bracketing
     * real time: the interval already happened, on another thread, and a
     * measure that re-timed it here would report the round trip as the hash.
     *
     * Guarded and bounded exactly as `digestOf` guards its own — `measure`
     * throws on a name clash in some engines, and an entry lives until
     * something clears it, so a page that ingests all day would otherwise
     * accumulate one per file forever.
     */
    if (reply.spent) emit(sentAt, now(), reply.spent);
    return { ref: reply.ref, bytes: reply.bytes };
  };

  /**
   * Delegated method by method, NOT `{ ...inner }`.
   *
   * `IndexedDbBlobs` is a class, so its `get`, `stat`, `delete` and `list` live
   * on the prototype and a spread of own enumerable properties silently drops
   * every one of them. The wrapper would have type-checked — the spread's type
   * is `BlobStore` — and then failed at runtime on the first repaint, which is
   * the worst combination available. `createMemoryBlobs` returns an object
   * literal and would have worked, so the unit suite would have been split down
   * the middle by backend.
   *
   * Written out rather than proxied for the same reason this package declares
   * its IndexedDB slice instead of importing one: four names is small enough to
   * read, and a `Proxy` would forward a method added later without anybody
   * deciding whether it belongs on this side of the boundary.
   */
  return {
    get: (id) => inner.get(id),
    stat: (id) => inner.stat(id),
    delete: (id) => inner.delete(id),
    list: () => inner.list(),
    derivatives: inner.derivatives,
    async putOwned(bytes, mime) {
      /**
       * A dead worker falls back to the store that was already working.
       *
       * @Codex, P2: *"every later upload is rejected for the lifetime of the
       * page."* That was the previous round's fix overshooting — it replaced a
       * hang with a permanent refusal, and a permanent refusal is not better
       * for the person whose next file will not save.
       *
       * `inner` is a complete, correct `BlobStore` and always was; the worker
       * only moves where one method runs. So the honest degradation is the one
       * `store-setup.ts` already claims for a missing `Worker`: ingest blocks
       * as much as it did before the worker existed, and nothing is lost.
       *
       * The one thing that cannot be recovered is a call whose bytes were
       * already in flight when the worker died — those are gone, and they still
       * report `SourceTransferLost`. This only covers the calls after it.
       *
       * `bytes` here is still the caller's, because nothing has been
       * transferred: `send` awaits the handshake before it posts.
       */
      if (dead !== undefined) return { ref: await inner.put(bytes, mime), bytes };
      try {
        return await send(bytes, mime);
      } catch (e) {
        /**
         * The FIRST file is the one that discovers the worker is dead, and it
         * should not be the one that pays for it.
         *
         * @Codex, P2 on #184: *"`send()` rejects with an ordinary `Error` while
         * the caller still owns the bytes, but this catch simply rethrows it …
         * the first ingest therefore still reports 'the file could not be
         * added' and requires the person to select it again, even though every
         * subsequent ingest falls back to `inner`."*
         *
         * Right, and it is the handshake fix stopping one step short. The whole
         * point of waiting for `ready` was that a caller keeps its bytes when
         * the worker never loads — and then this threw them away anyway, so the
         * person whose file went first got an error for a deploy problem while
         * everyone after them was served.
         *
         * The distinction the two error types were introduced for is exactly
         * what makes this safe: `SourceTransferLost` means the bytes are gone
         * and there is nothing to retry with. Anything else means we still have
         * them, so we write them the slow way and nobody notices.
         */
        /**
         * The RETURNED buffer, not the caller's — `postMessage` detached that
         * one, so `inner.put(bytes, ...)` here would store an empty array and
         * call it the person's file. The error carries the array precisely so
         * this line has something to write.
         */
        if (e instanceof SourceNotWritten) {
          return { ref: await inner.put(e.bytes, mime), bytes: e.bytes };
        }
        if (e instanceof SourceTransferLost) throw e;
        return { ref: await inner.put(bytes, mime), bytes };
      }
    },
    /**
     * The compatibility seam `0043` names, and it COPIES.
     *
     * A caller of `put` keeps its buffer — that is the contract the other
     * backends have always had — so the only way to transfer is to transfer
     * something else. 64 MiB costs about 40ms here and roughly 200ms on the
     * deployed machine, both well inside the budget, and a caller that minds
     * has `putOwned`.
     *
     * `Uint8Array<ArrayBuffer>` by construction rather than by cast: the copy
     * allocates its own buffer, which is what makes it transferable at all.
     */
    async put(bytes, mime) {
      /**
       * The `dead` check FIRST, before the copy.
       *
       * @Codex, P2 on `384b5df`: *"When the worker has already been marked dead
       * or stalled, every later compatibility `put()` still allocates and
       * copies the complete payload here before the `dead` check … immediately
       * discards that copy and calls `inner.put(bytes, mime)`. A caller writing
       * a ceiling-size blob therefore pays an avoidable 64 MiB main-thread
       * allocation and copy on every fallback write."*
       *
       * Right, and it is 64 MiB of main-thread work on the exact path
       * `0042`'s budget measures — paid on every write for the lifetime of a
       * page whose worker failed to load, which is precisely when the person is
       * already getting the slow path.
       *
       * The copy exists to have something transferable that is not the
       * caller's buffer; with no worker to transfer to, there is nothing for it
       * to be.
       */
      if (dead !== undefined) return inner.put(bytes, mime);
      const owned = new Uint8Array(bytes.byteLength);
      owned.set(bytes);
      /**
       * And a failure here is NOT a lost transfer, so it is RETRIED.
       *
       * Two findings, a round apart, and the second is the first one finished.
       *
       * @Codex, P2 on #184: *"this path has transferred only the newly
       * allocated `owned` copy, while the caller still holds the original
       * buffer as the `put` contract promises. Nevertheless, `send()` rejects
       * with `SourceTransferLost`, whose documented meaning is that the
       * caller's bytes are gone and must be reread; recovery code can therefore
       * treat a directly retryable write as destructive."*
       *
       * @Codex, P2 on `8e4315a`: *"the mandatory `BlobStore.put()`
       * compatibility path still rethrows here. If a caller starts `put()` and
       * the Worker then fails before announcing readiness, `send()` rejects
       * before transferring the copied buffer and marks the Worker dead; the
       * first call consequently fails even though `inner` is usable, while the
       * next call succeeds through the `dead` check above. Retry
       * `inner.put(bytes, mime)` here while the original bytes are still owned,
       * matching accepted decision 0043's every-caller failure contract."*
       *
       * The first round renamed the failure; this one removes it. Translating
       * `SourceTransferLost` into an ordinary `Error` told the caller their
       * bytes were retryable and then did not retry them — which is the same
       * shape as `putOwned`'s own finding two screens up, and it survived a
       * round precisely because the corrected error class read like the fix.
       *
       * So BOTH classes fall back here, and the reason is one line: `put`'s
       * contract is that the caller keeps its buffer, so `bytes` is intact on
       * every path out of `send`. What `send` may have consumed is `owned`, a
       * copy this method made and nobody else can see. There is no failure of
       * the worker that `put` cannot simply write the slow way.
       *
       * A duplicate write is not a hazard to weigh against that: the store is
       * content-addressed and `put` is idempotent for one `(bytes, mime)` pair,
       * so a worker that stores the bytes late lands on the row `inner` just
       * wrote rather than beside it.
       *
       * If `inner` fails too, ITS error propagates — that is the one naming the
       * durable failure, and the worker's reason is by then a description of a
       * path nobody is on.
       */
      try {
        return (await send(owned, mime)).ref;
      } catch {
        return inner.put(bytes, mime);
      }
    },
  };
}

/**
 * Put a measure on this thread's timeline for work that happened on another.
 *
 * The digest measure is placed at the END of the worker's interval rather than
 * at its start, because that is where it actually finished relative to the
 * round trip, and a phase table whose entries overlap incorrectly is the
 * `digest 741 beside store-source 270` problem this repo has already paid for.
 *
 * ## Anchored to the REPLY, not to the request
 *
 * @Codex, P2 on `802b4b2`: *"When concurrent uploads queue behind one another
 * in the serialized worker, `sentAt` is the time the later request was posted,
 * not when its `blobs.put()` began. Recording `spent.total` from that timestamp
 * places the later worker interval — and the digest interval derived from the
 * same origin — inside the predecessor's work, producing overlapping,
 * incorrectly ordered phase traces even though the worker executed them
 * sequentially."*
 *
 * Exact, and it is the same defect the paragraph above says this code already
 * paid for, one layer out: the entries do not overlap because two measures of
 * one request disagree, they overlap because two REQUESTS are drawn from the
 * same origin. The worker serialises, so the second request's work begins when
 * the first one's ends — a fact `sentAt` cannot express, because both were
 * posted before either ran.
 *
 * The worker finishes its work immediately before it replies, so ending the
 * interval at reply receipt is the closest placement the main thread can make.
 * It is off by the `postMessage` latency, which is small, does not scale with
 * the payload, and is the same for every request — so it shifts every interval
 * equally and orders them correctly, which is what a phase table is read for.
 *
 * `Math.max(sentAt, …)` because an interval may not begin before its request
 * was posted. That bound is the one thing this thread knows for certain, and it
 * is what keeps a clock skew or an overstated `spent.total` from drawing a
 * measure into a region where this request did not exist.
 *
 * The digest keeps its position relative to the END of the worker interval,
 * unchanged — re-anchoring moves where the pair sits, not how they sit together,
 * and changing both at once would silently move numbers already reported.
 */
function emit(
  sentAt: number,
  repliedAt: number,
  spent: { digest: number; total: number; bytes?: number },
): void {
  if (!hasPerformance() || typeof performance.measure !== 'function') return;
  try {
    if (performance.getEntriesByName('morph.store.worker', 'measure').length > 32) {
      performance.clearMeasures('morph.store.worker');
      performance.clearMeasures('morph.store.digest');
    }
    /**
     * The byte count rides on the measure as `detail`, which is what makes the
     * two requests of `0046`'s work control distinguishable at all.
     *
     * Task `0141` names the trap it replaces: `e2e/ingest-budget.mjs` collapses
     * phases to `Math.max` per name per pass, so a second `worker` measure
     * merges into the first — and a doubled request that fell back to the main
     * thread would leave `worker > 0` from the DOOR's request, with any clause
     * reading that phase passing. A guard green about the wrong question,
     * latent in the instrument rather than in the assertion.
     *
     * `detail` rather than a global counter: a measure is already the contract
     * *"any observer can read, including one that is not a test"*, it is
     * per-entry so nothing has to be reset between passes, and it cannot drift
     * out of step with the phase it describes because it IS the phase.
     */
    const began = Math.max(sentAt, repliedAt - spent.total);
    performance.measure('morph.store.worker', {
      start: began, duration: spent.total,
      ...(spent.bytes === undefined ? {} : { detail: { bytes: spent.bytes } }),
    });
    performance.measure('morph.store.digest', {
      start: began + Math.max(0, spent.total - spent.digest), duration: spent.digest,
    });
  } catch { /* a missing diagnostic must not fail a store write */ }
}

const describe = (event: unknown): string => {
  if (typeof event === 'string') return event;
  const e = event as { message?: unknown };
  return typeof e?.message === 'string' ? e.message : 'no reason given';
};

/* ────────────────────────────────────────────────────────── the worker side */

/**
 * The clock, guarded the way `blobs.ts` guards its own.
 *
 * `typeof performance === 'object'` rather than `typeof performance?.now`: the
 * optional-chaining form reads as the safe one and is not, because `typeof`
 * suppresses a ReferenceError only for a BARE identifier — a member expression
 * evaluates `performance` and throws first if it is undeclared.
 */
const hasPerformance = (): boolean =>
  typeof performance === 'object' && typeof performance.now === 'function';

const now = (): number => (hasPerformance() ? performance.now() : 0);

/**
 * The hashing this ONE request paid for, from the worker's own timeline.
 *
 * @Codex, P2 on #184: *"both requests sample this worker-global duration total,
 * so a later completion can include another request's hash in its own
 * `spent.digest`. The same subtraction becomes negative when `digestOf()`
 * clears its measure entries after the 33-entry bound."*
 *
 * The first half I had written down and accepted — *"the demo issues one source
 * at a time"* — which is a fact about today's only caller, not about the
 * function. The second half I had not seen, and it is worse than an
 * attribution error: `digestOf` clears its entries past 32, so a running total
 * can go DOWN between two reads and the subtraction reports a negative
 * duration. `performance.measure` then either throws or records nonsense, and
 * decision `0043`'s clause 4 asks for a phase report, not for a plausible one.
 *
 * Both go away by taking the entries that appeared DURING this request rather
 * than differencing a shared total: a set of names before, a set after, and the
 * durations of what is new. A concurrent request's measure is excluded because
 * it was not absent-then-present within this window... which is not quite true
 * either, so the window is narrowed by `startTime` as well: only entries that
 * began at or after this request did. Two puts that genuinely overlap still
 * cannot be told apart by a shared timeline — that is a property of
 * `performance`, not of this code — so the honest bound is "entries this
 * request could have caused", and it can no longer be negative.
 */
const digestSince = (from: number): number => {
  if (!hasPerformance() || typeof performance.getEntriesByName !== 'function') return 0;
  try {
    return performance.getEntriesByName('morph.store.digest', 'measure')
      .filter((e) => e.startTime >= from)
      .reduce((sum, e) => sum + e.duration, 0);
  } catch { return 0; }
};


/**
 * Serve `put` requests inside a worker, against a store this function opens.
 *
 * `open` is a thunk rather than a `BlobStore` so the connection is made once,
 * lazily, inside the worker — and so a failure to open is reported as a failed
 * request rather than as a worker that never answers.
 *
 * Nothing here writes a derivative or reads a blob. The worker's whole job is
 * the byte-proportional half of one source write.
 */
export function serveSourceStore(
  scope: WorkerScopeLike, open: () => Promise<BlobStore>,
): void {
  let opening: Promise<BlobStore> | undefined;
  /**
   * One source write at a time.
   *
   * @Codex, P2 on #184: *"the worker starts both asynchronous `put()` calls
   * concurrently. Each request subtracts a process-wide cumulative digest
   * total, so whichever finishes later includes the other request's hash
   * duration and can report `digest` longer than its own `store-source`
   * interval … measure the digest per request or serialize the worker's source
   * writes."*
   *
   * Windowing the measure by `startTime` narrowed this and did not close it: an
   * EARLIER request's window still contains a later one's hash, because the
   * window opens when the earlier request began. A shared timeline cannot
   * attribute overlapping work, so the fix is to stop overlapping.
   *
   * Serialising costs nothing that matters and is arguably right on its own
   * terms: these are byte-proportional operations on one thread, so two
   * concurrent 64 MiB hashes do not finish sooner than two sequential ones —
   * they finish at the same time as each other, which is later for the first
   * file and no earlier for the second. What it buys is that `digest`,
   * `store-source` and the worker interval describe one ingest, which is what
   * decision `0043` clause 4 asks the phase report to be.
   */
  let queue: Promise<unknown> = Promise.resolve();
  /**
   * Announced BEFORE the store is opened, and that is the point.
   *
   * This says "the module evaluated and my handler is installed", which is
   * exactly what a 404, a wrong MIME type or a syntax error prevents. Waiting
   * for `open()` would delay every first ingest behind an IndexedDB connection
   * for no extra safety: a database that fails to open is reported per request
   * and is retried, while a worker that never ran cannot report anything.
   */
  scope.postMessage({ morphSourceStore: 'ready' } satisfies ReadyNote);
  scope.onmessage = (event) => {
    if (!isRequest(event.data)) return;
    const { seq: at, bytes, mime } = event.data;
    // Chained, and the failure is swallowed here rather than allowed to poison
    // the queue: each request reports its own outcome to the caller below.
    queue = queue.then(() => (async () => {
      /**
       * The OPEN and the WRITE fail differently, and one catch conflated them.
       *
       * @Codex, P2 on #184: *"When `blobs.put()` rejects after `open()` has
       * already succeeded — for example, because a quota-limited write aborts —
       * this shared catch clears the resolved `opening` promise. The next
       * request opens another IndexedDB connection while the previous
       * `BlobStore` is discarded without any close operation, so repeated
       * upload retries under the same failure can accumulate live database
       * connections and browser resources."*
       *
       * Right, and the comment that justified the clearing said only *"a
       * failure to open should not pin the worker to a broken connection"* —
       * which is true, and was being applied to failures that were not
       * failures to open. A quota error is the case that makes it bite: the
       * person retries, every retry writes nothing and opens one more
       * connection, and the browser is worse off for each attempt.
       */
      let blobs;
      try {
        opening ??= open();
        blobs = await opening;
      } catch (e) {
        // The OPEN failed, so this connection is not one to keep. Cleared, and
        // the next request tries again — the original intent, now scoped to it.
        opening = undefined;
        /**
         * And the BYTES go back, because nothing here has touched them.
         *
         * `open()` runs before `blobs.put(bytes, mime)`, so on this path the
         * transferred array is exactly what arrived. Returning it is what lets
         * `sourceStoreOverWorker` fall back to `inner` for the file that
         * discovered the database would not open, instead of asking the person
         * to choose it again. @Codex, P2 on #184.
         *
         * The catch below deliberately does NOT do this — see its comment: a
         * failure after `put` began can leave the buffer detached or
         * half-cloned, and posting it there would sometimes throw inside the
         * catch and lose the reply as well.
         */
        scope.postMessage({
          morphSourceStore: 'put', seq: at, bytes,
          failed: e instanceof Error ? e.message : String(e),
        } satisfies StoreReply, [bytes.buffer]);
        return;
      }
      try {
        const began = now();
        // The digest's own measure, read out of the WORKER's timeline. It is
        // emitted by `digestOf` inside `put`, so there is no second hash and no
        // second 64 MiB pass — the same argument `blobs.ts` makes for measuring
        // it there rather than in the caller.
        const ref = await blobs.put(bytes, mime);
        // Clamped as well as windowed. A clock that goes backwards, or a
        // `measure` this request did not cause, must not produce a negative
        // duration — `performance.measure` rejects one, and losing the whole
        // phase report to a bad diagnostic is the tail wagging the dog.
        const spent = {
          digest: Math.max(0, digestSince(began)),
          total: Math.max(0, now() - began),
          // The STORE's count, and read before the `postMessage` below detaches
          // the buffer — `bytes.byteLength` is 0 on the far side of a transfer,
          // which `answerWithinFor` already carries a scar for.
          bytes: ref.bytes,
        };
        // The SAME buffer back. `put` copies on the way in — every backend
        // does, so that a caller reusing its buffer cannot change what the
        // digest names — so this array is still intact here.
        scope.postMessage(
          { morphSourceStore: 'put', seq: at, ref, bytes, spent } satisfies StoreReply,
          [bytes.buffer],
        );
      } catch (e) {
        /**
         * The bytes go back here TOO, and the paragraph this replaces was
         * wrong on a point of fact.
         *
         * It said *"a failure can leave the buffer detached, already
         * transferred into a structured clone, or simply untrustworthy"*.
         * @Codex, P2 on #184: *"`BlobStore.put` preserves its input and
         * IndexedDB structured cloning does not detach it … `send()`
         * misclassifies the recoverable write as `SourceTransferLost` and
         * `putOwned()` cannot retry through the fallback store."*
         *
         * Checkable rather than arguable, and it checks out: every backend
         * copies on the way in — the comment on the success path below says so
         * in the same breath — `digestOf` copies again, and a structured clone
         * reads a buffer without taking it. A rejected `put` therefore leaves
         * `bytes` exactly as it arrived.
         *
         * So the taxonomy is now about what HAPPENED to the bytes rather than
         * about where the failure was raised: `SourceTransferLost` is reserved
         * for an actual loss of the worker after the transfer, and everything
         * a store merely refused comes back with the array to retry with.
         *
         * The transfer is attempted and caught, which is the one true part of
         * the old paragraph kept as a guard rather than as a reason: if
         * posting the buffer ever did throw, losing the reply as well would
         * turn a refused write into a hang.
         *
         * The connection is KEPT. This catch covers writes only — the open has
         * its own above — and a store that opened is a store that works,
         * whatever one quota-limited write did.
         */
        const failed = e instanceof Error ? e.message : String(e);
        try {
          scope.postMessage(
            { morphSourceStore: 'put', seq: at, bytes, failed } satisfies StoreReply,
            [bytes.buffer],
          );
        } catch {
          scope.postMessage({ morphSourceStore: 'put', seq: at, failed } satisfies StoreReply);
        }
      }
    })().catch(() => undefined));
  };
}

/** Store bytes through `putOwned` when the store has it, and through `put`
 *  otherwise — the one line every caller would otherwise write, including the
 *  detail that the returned array is the one to keep using. */
export async function storeSource(
  blobs: BlobStore, bytes: Uint8Array<ArrayBuffer>, mime: string,
): Promise<{ ref: BlobRef; bytes: Uint8Array<ArrayBuffer> }> {
  if (blobs.putOwned) return blobs.putOwned(bytes, mime);
  return { ref: await blobs.put(bytes, mime), bytes };
}

